import { URLS } from 'api/constants';
import { ICasinoReview } from 'constants/types';
import { apiGet } from 'lib/api/client';
import { PaginationResponse } from 'lib/api/types';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useCasinoReviewsPaginationNoCache = ({
  casinoId,
  limit = 12,
}: {
  casinoId: string | string[] | undefined;
  limit?: number;
}) => {
  const [data, setData] = useState<ICasinoReview[]>([]);
  const [count, setCount] = useState(-1);
  const [isFetching, setIsFetching] = useState(false);

  const [page, setPage] = useState(
    data.length ? Math.ceil(data.length / limit) : 1
  );

  const resetData = useCallback(() => {
    setPage(1);
    setData([]);
    setCount(-1);
  }, []);

  const refetchData = useCallback(() => {
    if (!casinoId) {
      return;
    }

    setIsFetching(true);
    apiGet<PaginationResponse<ICasinoReview>>(
      `${URLS.CASINO_REVIEWS_BY_CASINO}${casinoId.toString()}`,
      { page, limit }
    )
      .then((response) => {
        const results = response.results ?? [];
        setData((current) => (page === 1 ? results : [...current, ...results]));
        setCount(Number(response.count ?? results.length));
      })
      .finally(() => setIsFetching(false));
  }, [casinoId, limit, page]);

  useEffect(() => {
    resetData();
  }, [resetData]);

  useEffect(() => {
    if (casinoId) {
      refetchData();
    }
  }, [casinoId, refetchData]);

  const isLast = useMemo(() => {
    if (count === -1) {
      return false;
    }
    return data.length >= count;
  }, [count, data.length]);

  return {
    page,
    data,
    isLast,
    setPage,
    resetData,
    isFetching,
    refetchData,
    count: count < 0 ? 0 : count,
  };
};
